"use client";

import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { useKonamiCode } from "@/hooks/useKonamiCode";

export const BREATHING_EXERCISE_EVENT = "trigger-breathing-exercise";

const TOTAL_CYCLES = 3;
type Phase = "in" | "hold" | "out";

const PHASE_SEQUENCE: Phase[] = ["in", "hold", "out"];
const PHASE_DURATIONS: Record<Phase, number> = { in: 4, hold: 7, out: 8 };
const PHASE_LABEL: Record<Phase, string> = {
  in: "Breathe in",
  hold: "Hold",
  out: "Breathe out",
};
const PHASE_GRAD: Record<Phase, [string, string]> = {
  in: ["#5eead4", "#818cf8"],
  hold: ["#a78bfa", "#c084fc"],
  out: ["#93c5fd", "#34d399"],
};
const PHASE_TEXT_COLOR: Record<Phase, string> = {
  in: "#99f6e4",
  hold: "#ddd6fe",
  out: "#bfdbfe",
};
const CYCLE_MESSAGES: Record<number, string> = {
  1: "Follow the circle with your breath",
  2: "You're doing great — stay with it",
  3: "Last round. Almost there",
};

const RING_R = 108;
const RING_C = +(2 * Math.PI * RING_R).toFixed(2);

export default function BreathingExercise() {
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>("in");
  const [cycle, setCycle] = useState(1);
  const [secondsLeft, setSecondsLeft] = useState(PHASE_DURATIONS.in);
  const [done, setDone] = useState(false);
  const [orbScale, setOrbScale] = useState(1);
  const [orbMs, setOrbMs] = useState(0);
  const [ringOffset, setRingOffset] = useState(RING_C);
  const [ringMs, setRingMs] = useState(0);

  const phaseTimer = useRef<ReturnType<typeof setTimeout>>();
  const countTimer = useRef<ReturnType<typeof setInterval>>();
  const r1Ref = useRef<number>();
  const r2Ref = useRef<number>();

  const clearAll = () => {
    clearTimeout(phaseTimer.current);
    clearInterval(countTimer.current);
    if (r1Ref.current) cancelAnimationFrame(r1Ref.current);
    if (r2Ref.current) cancelAnimationFrame(r2Ref.current);
  };

  const reset = () => {
    clearAll();
    setStarted(false);
    setPhase("in");
    setCycle(1);
    setDone(false);
    setSecondsLeft(PHASE_DURATIONS.in);
    setOrbScale(1);
    setOrbMs(0);
    setRingOffset(RING_C);
    setRingMs(0);
  };

  const close = () => {
    clearAll();
    setOpen(false);
  };

  useKonamiCode(() => { reset(); setOpen(true); });

  useEffect(() => {
    const fn = () => { reset(); setOpen(true); };
    window.addEventListener(BREATHING_EXERCISE_EVENT, fn);
    return () => window.removeEventListener(BREATHING_EXERCISE_EVENT, fn);
  }, []);

  // Animate orb scale + ring fill on each phase change
  useEffect(() => {
    if (!open || !started || done) return;
    const ms = PHASE_DURATIONS[phase] * 1000;

    // Snap to start position immediately (no transition)
    setOrbMs(0);
    setOrbScale(phase === "out" ? 1.6 : 1);
    setRingMs(0);
    setRingOffset(RING_C);

    // Animate to target on next two paints (two-frame trick for reliable transitions)
    r1Ref.current = requestAnimationFrame(() => {
      r2Ref.current = requestAnimationFrame(() => {
        if (phase !== "hold") {
          setOrbMs(ms);
          setOrbScale(phase === "in" ? 1.6 : 1);
        }
        setRingMs(ms);
        setRingOffset(0);
      });
    });

    return () => {
      if (r1Ref.current) cancelAnimationFrame(r1Ref.current);
      if (r2Ref.current) cancelAnimationFrame(r2Ref.current);
    };
  }, [phase, open, started, done]);

  // Per-second countdown
  useEffect(() => {
    if (!open || !started || done) return;
    setSecondsLeft(PHASE_DURATIONS[phase]);
    countTimer.current = setInterval(
      () => setSecondsLeft(s => Math.max(0, s - 1)),
      1000
    );
    return () => clearInterval(countTimer.current);
  }, [phase, open, started, done]);

  // Advance phase after duration
  useEffect(() => {
    if (!open || !started || done) return;
    const ms = PHASE_DURATIONS[phase] * 1000;
    phaseTimer.current = setTimeout(() => {
      const idx = PHASE_SEQUENCE.indexOf(phase);
      if (idx === PHASE_SEQUENCE.length - 1) {
        if (cycle >= TOTAL_CYCLES) {
          setDone(true);
        } else {
          setCycle(c => c + 1);
          setPhase("in");
        }
      } else {
        setPhase(PHASE_SEQUENCE[idx + 1]);
      }
    }, ms);
    return () => clearTimeout(phaseTimer.current);
  }, [open, started, phase, cycle, done]);

  // Auto-close 5 s after completion
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(close, 5000);
    return () => clearTimeout(t);
  }, [done]);

  // Escape key
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (open && e.key === "Escape") close(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open]);

  if (!open) return null;

  const [c1, c2] = PHASE_GRAD[phase];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Breathing exercise"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-10 bg-slate-950/95 backdrop-blur-2xl animate-in fade-in duration-500"
    >
      <button
        onClick={close}
        aria-label="Close"
        className="absolute right-6 top-6 flex h-9 w-9 items-center justify-center rounded-full text-white/30 transition hover:bg-white/10 hover:text-white"
      >
        <X size={18} />
      </button>

      {done ? (
        <div className="flex flex-col items-center gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div
            className="flex h-20 w-20 items-center justify-center rounded-full"
            style={{
              background: "radial-gradient(circle at 38% 35%, #5eead4, #818cf8)",
              boxShadow: "0 0 60px rgba(94,234,212,0.4)",
            }}
          >
            <Check size={32} className="text-white" />
          </div>
          <p className="font-crimsonText text-4xl text-white">Well done.</p>
          <p className="font-onest max-w-xs text-center text-sm leading-relaxed text-white/50">
            Take a moment to notice how you feel. You can come back to this anytime.
          </p>
        </div>
      ) : !started ? (
        <div className="flex flex-col items-center gap-8 animate-in fade-in duration-500">
          <div className="flex flex-col items-center gap-3">
            <p className="font-onest text-xs uppercase tracking-[0.25em] text-white/30">
              Guided Breathing
            </p>
            <p className="font-crimsonText text-4xl text-white sm:text-5xl">
              Take a moment.
            </p>
            <p className="font-onest max-w-xs text-center text-sm leading-relaxed text-white/50">
              3 rounds of box breathing — inhale, hold, exhale. Takes about 1 minute.
            </p>
          </div>

          {/* Static orb preview */}
          <div className="relative flex h-40 w-40 items-center justify-center">
            <div
              className="absolute inset-0 rounded-full blur-3xl"
              style={{ background: "radial-gradient(circle, #5eead455 0%, #818cf833 50%, transparent 70%)" }}
            />
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 256 256">
              <circle
                cx={128} cy={128} r={RING_R}
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth={2}
              />
            </svg>
            <div
              className="h-16 w-16 rounded-full"
              style={{
                background: "radial-gradient(circle at 38% 35%, #5eead4, #818cf8)",
                boxShadow: "0 0 30px #5eead488, 0 0 60px #818cf844",
              }}
            />
          </div>

          <button
            onClick={() => setStarted(true)}
            className="font-onest rounded-full px-10 py-3 text-sm font-medium text-slate-950 transition-all hover:scale-105 active:scale-95"
            style={{
              background: "linear-gradient(135deg, #5eead4, #818cf8)",
              boxShadow: "0 0 24px #5eead455",
            }}
          >
            Begin
          </button>
        </div>
      ) : (
        <>
          <p className="font-onest text-xs uppercase tracking-[0.25em] text-white/30">
            Guided Breathing
          </p>

          {/* Orb + progress ring */}
          <div className="relative flex h-64 w-64 items-center justify-center">
            {/* Ambient glow */}
            <div
              className="absolute inset-0 rounded-full blur-3xl"
              style={{
                transform: `scale(${orbScale})`,
                transition: `transform ${orbMs}ms ease-in-out`,
                background: `radial-gradient(circle, ${c1}55 0%, ${c2}33 50%, transparent 70%)`,
              }}
            />

            {/* SVG progress ring */}
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 256 256">
              <defs>
                <linearGradient id="rg" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={c1} />
                  <stop offset="100%" stopColor={c2} />
                </linearGradient>
              </defs>
              {/* Track */}
              <circle
                cx={128} cy={128} r={RING_R}
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth={2}
              />
              {/* Fill */}
              <circle
                cx={128} cy={128} r={RING_R}
                fill="none"
                stroke="url(#rg)"
                strokeWidth={2.5}
                strokeLinecap="round"
                style={{
                  strokeDasharray: RING_C,
                  strokeDashoffset: ringOffset,
                  transition: `stroke-dashoffset ${ringMs}ms linear`,
                  transform: "rotate(-90deg)",
                  transformOrigin: "128px 128px",
                }}
              />
            </svg>

            {/* Orb */}
            <div
              className="h-20 w-20 rounded-full"
              style={{
                transform: `scale(${orbScale})`,
                transition: `transform ${orbMs}ms ease-in-out`,
                background: `radial-gradient(circle at 38% 35%, ${c1}, ${c2})`,
                boxShadow: `0 0 30px ${c1}88, 0 0 60px ${c2}44`,
              }}
            />

            {/* Countdown number, fixed at center, does not scale with orb */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span
                className="font-onest text-2xl font-light tabular-nums text-white/80"
                style={{ textShadow: "0 1px 8px rgba(0,0,0,0.7)" }}
              >
                {secondsLeft}
              </span>
            </div>
          </div>

          {/* Phase label + encouragement */}
          <div className="flex flex-col items-center gap-1.5">
            <p
              className="font-crimsonText text-3xl sm:text-4xl"
              style={{
                color: PHASE_TEXT_COLOR[phase],
                transition: "color 600ms ease",
              }}
            >
              {PHASE_LABEL[phase]}
            </p>
            <p className="font-onest text-xs text-white/40">
              {CYCLE_MESSAGES[cycle]}
            </p>
          </div>

          {/* Cycle progress dots */}
          <div className="flex items-center gap-2.5">
            {Array.from({ length: TOTAL_CYCLES }, (_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all duration-500"
                style={{
                  width: i + 1 === cycle ? "28px" : i + 1 < cycle ? "16px" : "6px",
                  background:
                    i + 1 < cycle
                      ? "rgba(255,255,255,0.45)"
                      : i + 1 === cycle
                      ? "rgba(255,255,255,0.85)"
                      : "rgba(255,255,255,0.15)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
